import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Clipboard } from '@angular/cdk/clipboard';
import { ComponentType } from '@angular/cdk/portal';
import { MatDialog } from '@angular/material/dialog';

import { combineLatest, firstValueFrom, timer } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

import { GlobalFunctions } from '@global/GlobalFunctions';

import { convertEpochToDateString, generateGUID } from '@functions/functions';

import { AuthenticationService, IWebAppUser } from '@services/authentication.service';
import { Account, IAccount } from '@services/accounts.service';
import { IntegrationsService, IIntegrationType, IWebhookIntegration } from '@services/integrations.service';
import { NeuronsensorsService, nsWebhookConfig } from '@services/neuronsensors.service';
import { IOrganisation } from '@services/organisation-data.service';
import { siteSubscriptions } from '@services/siteSubscriptions';

import { EditWebhookIntegrationComponent } from '@dialogs/edit-webhook-integration/edit-webhook-integration.component';
import { EditWebhookIntegrationNeuronSensorComponent } from '@dialogs/edit-webhook-integration-neuronsensor/edit-webhook-integration-neuronsensor.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { SelectEventArgs, TabComponent } from '@syncfusion/ej2-angular-navigations';

/** DataIntegrationsComponent */
@Component({
	selector: 'app-data-integrations',
	templateUrl: './data-integrations.component.html',
	styleUrls: ['./data-integrations.component.scss']
})
export class DataIntegrationsComponent implements OnInit {

	/** Route Organisation ID */
	public routeOrganisationID: string

	/** Selected Organisation */
	public selectedOrganisation: IOrganisation

	/** Selected Account GUID */
	public selectedAccountGUID: string

	/** Selected Account */
	public selectedAccount: IAccount

	/** Array of Webhooks */
	public webhookIntegrations: IWebhookIntegration[]

	/** Array of Webhook Types */
	public webhookIntegrationTypes: IIntegrationType[]

	/** Webhook Types */
	public webhookTypes: { [key: string]: any } = {}

	/** Webhooks Loaded */
	public WebhooksLoaded: boolean

	/** Current User */
	public currentUser: IWebAppUser

	/** Observable Subject Finalise */
	private destroyRef = inject(DestroyRef);

	/** Selected Integration ID */
	public selectedIntegrationID: string = "";

	/** Selected Integration */
	public selectedIntegration?: IWebhookIntegration

	// set the value to select an item based on mapped value at initial rendering
	private ShowWebhooksKey: string = this.constructor.name + "_showWebhooks"
	/** showWebhooks */
	public showWebhooks: string = localStorage.getItem(this.ShowWebhooksKey) || "1";

	// define the JSON of data for th eButton Group
	public BGSelection: {
		Id: string;
		Value: string;
		Text: string;
		Icon: string;
		Color: string;
	}[] = [
			{ Id: '1', Value: 'All', Text: 'Showing All Webhooks', Icon: '', Color: '' },
			{ Id: '2', Value: 'Enabled', Text: 'Showing Only Enabled Webhooks', Icon: 'toggle_on', Color: 'green' },
			{ Id: '3', Value: 'Disabled', Text: 'Showing Only Disabled Webhooks', Icon: 'toggle_off', Color: 'red' }
		];

	/** selectedTab */
	public selectedTab: number = 0

	/** NeuronSensorConfig */
	public NeuronSensorConfig: nsWebhookConfig[]

	/** Neuron Sensor validation cache duration */
	private readonly validationCacheDuration = 5 * 60 * 1000;

	/** validationCache */
	private validationCache = new Map<string, {
		config: nsWebhookConfig;
		timestamp: number;
	}>();

	/** Supress Tab Change */
	private suppress = false;

	/** webhookTabs */
	@ViewChild('webhookTabs') webhookTabs!: TabComponent;

	private webhooktypeid: number = 0;

	/** refreshData */
	private refreshData: any;

	/**
	 * Default Constructor
	 * @param globalFunctions Global Functions
	 * @param authenticationService Authentication Services
	 * @param activatedRoute ActivatedRoute
	 * @param siteSubscriptions Site Subscriptions
	 * @param integrationService integrationService
	 * @param router router
	 * @param dialog MatDialog
	 * @param clipboard Clipboard
	 * @param neuronsensorsService NeuronsensorsService
	 */
	constructor(
		private globalFunctions: GlobalFunctions,
		public authenticationService: AuthenticationService,
		private activatedRoute: ActivatedRoute,
		private siteSubscriptions: siteSubscriptions,
		private integrationService: IntegrationsService,
		private router: Router,
		public dialog: MatDialog,
		private clipboard: Clipboard,
		private neuronsensorsService: NeuronsensorsService
	) {

	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit() {
		this.globalFunctions.displaySpinner(true);

		const childRoute = this.getChildRoute(this.activatedRoute);

		/*
		 * Initialise the selected Account / Organisation.
		 * These values are required before we can load the integrations.
		 */
		combineLatest([
			this.authenticationService.currentUser$,
			this.siteSubscriptions.myAccounts$,
			this.siteSubscriptions.myOrganisations$,
			this.activatedRoute.paramMap
		]).pipe(
			takeUntilDestroyed(this.destroyRef),
			filter(([currentUser, myAccounts, myOrganisations, paramMap]) =>
				!!currentUser && !!myAccounts && !!myOrganisations && !!paramMap
			)
		).subscribe({
			next: async ([currentUser, myAccounts, myOrganisations, paramMap]) => {

				this.suppress = true

				// Check we have a validated User
				if (currentUser) {
					this.currentUser = <IWebAppUser>currentUser;
				}

				// Get the GUID for the selected Organisation to Manage
				if (paramMap.has('organisation')) {

					if (this.routeOrganisationID != paramMap.get('organisation')) {

						this.routeOrganisationID = paramMap.get('organisation') || "";

						if (this.routeOrganisationID != "") {

							const organisationsList = myOrganisations;

							this.selectedOrganisation = <IOrganisation>organisationsList.find(
								({ Uid }) => Uid.toUpperCase() === this.routeOrganisationID.toUpperCase()
							);
						}

						const selectedAccount = myAccounts.find(
							a => a.GUID == this.selectedOrganisation.AccountGUID
						);

						if (selectedAccount) {
							this.selectedAccount = this.siteSubscriptions.normalizeFeatures(selectedAccount) as Account;

							this.selectedAccountGUID = selectedAccount.GUID;
						}

						await this.getIntegrationTypes(
							this.selectedAccountGUID,
							this.selectedOrganisation.Uid
						)
						await this.getIntegrations(
							this.selectedAccountGUID,
							this.selectedOrganisation.Uid
						)

						this.WebhooksLoaded = true;
												
						setTimeout(() => {
							this.suppress = false
							this.subscribeQueryParams()
						}, 500);

					}
				}
			},
			error: (error) => {
				this.globalFunctions.HandleError(
					error,
					`Error: ${this.constructor.name} → account/organisation initialisation`,
					false
				);
			}
		});

		/*
		 * The child route controls the selected Integration.
		 * Only react when the actual integrationID changes.
		 */
		childRoute.paramMap.pipe(
			map(params => params.get('integrationID') || ''),
			distinctUntilChanged(),
			takeUntilDestroyed(this.destroyRef)
		).subscribe({
			next: (integrationID) => {
				this.selectedIntegrationID = integrationID;
			},
			error: (error) => {
				this.globalFunctions.HandleError(
					error,
					`Error: ${this.constructor.name} → child route parameters`,
					false
				);
			}
		});

		/** Create a refresh timer for the webhooks */
		this.refreshData = timer(60000, 60000).pipe(
			takeUntilDestroyed(this.destroyRef)
		).subscribe(async () => {

			if (!this.selectedAccountGUID || !this.routeOrganisationID || this.suppress) {
				return;
			}

			this.suppress = true;

			try {
				await Promise.all([
					this.getIntegrationTypes(
						this.selectedAccountGUID,
						this.selectedOrganisation.Uid
					),

					this.getIntegrations(
						this.selectedAccountGUID,
						this.routeOrganisationID
					)
				]);
			}
			finally {
				this.suppress = false;
			}

		});

	}

	/** subscribeQueryParams */
	subscribeQueryParams() {

		/*
		* Query parameters control the selected tab.
		* Only react when webhooktypeid changes.
		*/
		this.activatedRoute.queryParamMap.pipe(
			map(params => ({
				webhooktypeid: params.get('webhooktypeid')
			})),
			distinctUntilChanged(
				(previous, current) =>
					previous.webhooktypeid === current.webhooktypeid
			),
			takeUntilDestroyed(this.destroyRef)
		).subscribe({
			next: ({ webhooktypeid }) => {
				if (this.webhookIntegrationTypes && webhooktypeid && this.webhookTabs) {

					this.webhooktypeid = parseInt(webhooktypeid)

					this.suppress = true;

					const tabItems = this.webhookTabs.items;
					const whType = this.webhookIntegrationTypes.find(t => t.WebhookTypeID == parseInt(webhooktypeid))

					const idx = tabItems.findIndex((item: any) =>
						item.properties.id == whType?.Type
					);
					if (this.selectedTab != Math.max(idx, 0)) {
						this.selectedTab = Math.max(idx, 0);						
					}
					queueMicrotask(() => this.suppress = false);
				}
			},
			error: (error) => {
				this.globalFunctions.HandleError(
					error,
					`Error: ${this.constructor.name} → query parameters`,
					false
				);
			}
		});

	}

	/**
	 * getChildRoute
	 * @param route 
	 * @returns 
	 */
	private getChildRoute(route: ActivatedRoute): ActivatedRoute {
		while (route.firstChild) {
			route = route.firstChild;
		}

		return route;
	}

	/**
	 * ValidateNeuronSensorIntegration
	 * @param integration 
	 * @returns 
	 */
	ValidateNeuronSensorIntegration(integration: IWebhookIntegration): string {

		if (integration.NeuronSensors) {
			if (this.validationCache.has(integration.ClientWebhookID)) {
				const cached = this.validationCache.get(integration.ClientWebhookID);
				if (cached) {
					const result = cached.config;
					if (result.disabled >= 100)
						return "Disabled"
					else if (result.disabled >= 50 && result.disabled < 100)
						return "Warning"
					else
						return "OK"
				} else {
					return ""
				}
			}
		}
		return "";
	}

	/**
	 * getIntegrations
	 * @param integration 
	 * @returns 
	 */
	async getNeuronSensorIntegration(integration: IWebhookIntegration): Promise<nsWebhookConfig | undefined> {

		var apiKey = integration.NeuronSensorAPI
		var systemID = integration.NeuronSensorSystem
		var webhookID = integration.ClientWebhookID

		try {
			const integration = await firstValueFrom(
				this.neuronsensorsService.getIntegrationByID$(apiKey, systemID, webhookID)
					.pipe(
						filter((result) => !!result)
					)
			);

			return integration

		} catch (error: any) {
			if (error.status != 404) {
				this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → this.neuronsensorsService.getIntegrationByID$`, false);
			}
			return undefined;
		}
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	};

	/** SelectRow */
	SelectRow(integration: IWebhookIntegration) {
		this.selectedIntegration = integration
	};

	/**
	 * toggleButtonGroupFilter
	 * @param show 
	 */
	toggleButtonGroupFilter(show: string): void {
		this.showWebhooks = show;
		localStorage.setItem(this.ShowWebhooksKey, this.showWebhooks);
		this.updateWebhookLists(this.showWebhooks)
	}

	/**
	 * getIntegrationTypes
	 * @param accountGUID
	 * @param organisationUID
	 */
	async getIntegrationTypes(accountGUID: string, organisationUID: string) {

		let params = {
			"accountGUID": accountGUID,
			"organisationUID": organisationUID
		}

		try {
			const integrationTypes = await firstValueFrom(
				this.integrationService.getWebhookIntegrationTypes$(params).pipe(
					filter((integrationTypes) => !!integrationTypes))
			);

			if (integrationTypes) {
				if (JSON.stringify(this.webhookIntegrationTypes) !== JSON.stringify(integrationTypes)) {
					this.webhookIntegrationTypes = <IIntegrationType[]>integrationTypes;
				}
			}
		}
		catch (error) {
			// console.log(error);
			this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → this.integrationService.getWebhookIntegrationTypes$`, false);
		}

	};

	/**
	 * Get the Integrations for the Account Organisation filtered by Type
	 * @param accountGUID
	 * @param organisationUID
	 * @param typeID
	 */
	async getIntegrations(accountGUID: string, organisationUID: string, typeID: number = 0) {

		let queryParams = {
			"typeID": typeID
		}

		try {
			const integrations = await firstValueFrom(
				this.integrationService.getWebhookIntegrations$(accountGUID, organisationUID, "", queryParams).pipe(
					filter((integrations) => !!integrations))
			);

			if (integrations) {

				if (integrations.length == 0) {
					this.webhookIntegrations = [];
					this.WebhooksLoaded = false;
					return;
				}

				const changed =
					this.webhookIntegrations?.length !== integrations.length ||
					integrations.some((item: IWebhookIntegration, index: number) =>
						item.Enabled !== this.webhookIntegrations[index]?.Enabled ||
						item.Deleted !== this.webhookIntegrations[index]?.Deleted ||
						item.Description !== this.webhookIntegrations[index]?.Description ||
						item.SensorOnlineCount !== this.webhookIntegrations[index]?.SensorOnlineCount ||
						item.DatapointOnlineCount !== this.webhookIntegrations[index]?.DatapointOnlineCount ||
						item.Offline !== this.webhookIntegrations[index]?.Offline ||
						item.LastValueReceived !== this.webhookIntegrations[index]?.LastValueReceived
					);

				if (changed) {
					this.webhookIntegrations = <IWebhookIntegration[]>integrations;
					this.updateWebhookLists(this.showWebhooks);


					if (this.selectedIntegrationID != "") {
						this.selectedIntegration = this.webhookIntegrations.find(
							(x: IWebhookIntegration) => x.WebhookUID == this.selectedIntegrationID
						);
					}
				}
				this.NeuronSensorConfig = [] as nsWebhookConfig[];

				const neuronIntegrations = this.webhookIntegrations.filter(
					integration => integration.NeuronSensors
				);

				if (neuronIntegrations.length > 0) {

					await Promise.all(
						neuronIntegrations.map(async integration => {

							const cached = this.validationCache.get(integration.ClientWebhookID);

							if (cached && Date.now() - cached.timestamp < this.validationCacheDuration) {
								return;
							}

							const rtn = await this.getNeuronSensorIntegration(integration);

							if (rtn) {
								this.validationCache.set(integration.ClientWebhookID, {
									config: rtn,
									timestamp: Date.now()
								});
							}
							else {
								this.validationCache.delete(integration.ClientWebhookID);
							}
						})
					);

				}
				else {
					this.validationCache.clear();
				}
			}
		}
		catch (error) {
			this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → this.integrationService.getWebhookIntegrations$`, false);
		} finally { this.globalFunctions.displaySpinner(false); }

	};

	/**
	 * toggleChange
	 * @param event 
	 * @param integration 
	 */
	toggleChange(event: MouseEvent, integration: IWebhookIntegration) {

		// Prevent the toggle from changing immediately
		event.preventDefault();
		event.stopPropagation();

		// Check if webhook is enabled
		const enabled = integration.Enabled;
		this.globalFunctions.UserConfirm$(`${enabled ? "Disable" : "Enable"} ${integration.Description} webhook?`, `${enabled ? "Disable" : "Enable"} Webhook`, "Yes", "No")
			.subscribe(async confirmed => {
				if (confirmed) {

					try {
						integration.Enabled = !integration.Enabled

						const integrations = await firstValueFrom(
							this.integrationService.patchWebhookIntegration$(this.selectedAccountGUID, this.selectedOrganisation.Uid, integration.OrganisationWebhookID, integration).pipe(
								filter((result) => !!result))
						);

						if (integrations) {
							let data = <IWebhookIntegration>integrations
							Object.assign(integration, data);

							if (data.Enabled) {
								this.globalFunctions.DisplaySnackBar({ message: `Webhook ${data.Description} had been Enabled`, action: "" })
								if (data.NeuronSensors) {
									this.globalFunctions.UserConfirm$(`Re-Enable Webhook in Neuron Sensors?`, "Enable NeuronSensors", "Yes", "No")
										.subscribe(async confirmed => {
											if (confirmed) {
												var nsIntegration = {} as nsWebhookConfig
												nsIntegration.id = data.ClientWebhookID
												nsIntegration.type = "POST"
												nsIntegration.disabled = 0
												await this.patchNeuronSensors(data.NeuronSensorAPI, data.NeuronSensorSystem, nsIntegration)
											}
										});
								}
							}
							else {
								this.globalFunctions.DisplaySnackBar({ message: `Webhook ${data.Description} had been Disabled`, action: "" })
								if (data.NeuronSensors) {
									// If deleted or Not Enabled, then automatically disable the Webhook
									var nsIntegration = {} as nsWebhookConfig
									nsIntegration.id = data.ClientWebhookID
									nsIntegration.type = "POST"
									nsIntegration.disabled = 150
									await this.patchNeuronSensors(data.NeuronSensorAPI, data.NeuronSensorSystem, nsIntegration)
								}
							}
						}

					}
					catch (error) {
						this.globalFunctions.HandleError(error, "patchWebhookIntegration Error: integrationService.patchWebhookIntegration$", false)
					}
				}
			})
	};

	/**
	 * updateWebhookLists
	 * @param showWebhooks 
	 */
	updateWebhookLists(showWebhooks: string) {

		if (!this.webhookIntegrations || !this.webhookIntegrationTypes) {
			return;
		}

		const webhookTypes: { [key: string]: any } = {};

		for (const integration of this.webhookIntegrationTypes) {

			const typeID = integration.WebhookTypeID;

			let result = this.webhookIntegrations.filter(
				({ WebhookTypeID }) => WebhookTypeID == typeID
			);

			if (showWebhooks == "2") {
				result = result.filter(webhook => webhook.Enabled == true);
			}
			else if (showWebhooks == "3") {
				result = result.filter(webhook => webhook.Enabled == false);
			}

			webhookTypes[typeID] = {
				webhooks: result
			};
		}

		this.webhookTypes = webhookTypes;
	}

	/**
	 * addIntegration
	 * @param typeID 
	 */
	addIntegration(typeID: number) {

		// alert("Add a new Webhook Integration: Not Implemeneted Yet");
		try {
			let integrationType = <IIntegrationType>this.webhookIntegrationTypes.find(({ WebhookTypeID }) => WebhookTypeID == typeID)
			let WebhookUID = generateGUID()
			// Create a 'Local' copy of the integration Information
			var _edit = <IWebhookIntegration>{
				AccountGUID: this.selectedAccountGUID,
				OrganisationWebhookID: 0,
				OrganisationID: this.selectedOrganisation.Id,
				OrganisationUID: this.selectedOrganisation.Uid,
				WebhookID: 0,
				WebhookUID: WebhookUID,
				ClientWebhookID: "",
				Host: (integrationType.Host),
				Path: (integrationType.Path + WebhookUID),
				FQN: (integrationType.Host + integrationType.Path),
				Description: "",
				AutoBuild: false,
				Enabled: true,
				WebhookTypeID: integrationType.WebhookTypeID,
				Type: integrationType.Type,
				TypePath: integrationType.Path,
				TypeDescription: integrationType.Description,
				TypeAzureCode: integrationType.AzureCode,
				Deleted: false,
				SensorCount: 0,
				SensorOnlineCount: 0,
				DatapointCount: 0,
				DatapointOnlineCount: 0,
				NeuronSensorAPI: "",
				NeuronSensorSystem: "",
				NeuronSensors: integrationType.NeuronSensors
			};

			// Choose the dialog component
			const component: ComponentType<any> = _edit.NeuronSensors
				? EditWebhookIntegrationNeuronSensorComponent
				: EditWebhookIntegrationComponent;

			// Display the dialog box
			const dialogRef = this.dialog.open(component, { data: _edit });

			// Update the original Asset with the result
			dialogRef.afterClosed().pipe(
				filter(result => !!result)
			).subscribe(async result => {
				try {
					// Check we have a result to work with
					this.globalFunctions.displaySpinner(true)
					Object.assign(_edit, result);

					const integration = await firstValueFrom(
						this.integrationService.postWebhookIntegration$(this.selectedAccountGUID, this.selectedOrganisation.Uid, _edit).pipe(
							filter((result) => !!result))
					);

					if (integration) {
						let data = <IWebhookIntegration>integration
						await this.getIntegrationTypes(this.selectedAccountGUID, this.selectedOrganisation.Uid)

						// If deleted or Not Enabled, then automatically disable the Webhook
						if (data.Deleted || !data.Enabled) {
							var nsIntegration = {} as nsWebhookConfig
							nsIntegration.id = data.ClientWebhookID
							nsIntegration.type = "POST"
							nsIntegration.disabled = 150
							await this.patchNeuronSensors(data.NeuronSensorAPI, data.NeuronSensorSystem, nsIntegration)
						}

						this.globalFunctions.DisplaySnackBar({ message: `Webhook ${data.Description} had been Added`, action: "" })
					}
				}
				catch (error) { this.globalFunctions.HandleError(error, "addIntegration Error: this.integrationService.postWebhookIntegration$", false) }
				finally { this.globalFunctions.displaySpinner(false); }
			});
		}
		catch (error) { this.globalFunctions.HandleError(error, "addIntegration Error: Adding Integration", false) }
	};

	/**
	 * editIntegration
	 * @param integration 
	 */
	editIntegration(integration: IWebhookIntegration) {

		// Create a 'Local'/shallow copy of the integration Information
		var _edit = { ...integration }

		//Pre populate this 
		if (!_edit.AccountGUID || _edit.AccountGUID == "") {
			_edit.AccountGUID = this.selectedAccountGUID
		}

		// Choose the dialog component
		const component: ComponentType<any> = _edit.NeuronSensors
			? EditWebhookIntegrationNeuronSensorComponent
			: EditWebhookIntegrationComponent;

		// Display the dialog box
		const dialogRef = this.dialog.open(component, { data: _edit });

		// Update the original Asset with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(async result => {
			try {
				// Check we have a result to work with
				this.globalFunctions.displaySpinner(true)
				Object.assign(_edit, result);

				/** Assign new result to integration */
				const asyncResult = await firstValueFrom(
					this.integrationService.patchWebhookIntegration$(this.selectedAccountGUID, this.selectedOrganisation.Uid, _edit.OrganisationWebhookID, _edit).pipe(
						filter((result) => !!result))
				);

				if (asyncResult) {
					let data = <IWebhookIntegration>asyncResult
					Object.assign(integration, data);
					// If deleted or Not Enabled, then automatically disable the Webhook
					if (data.Deleted || !data.Enabled) {
						var nsIntegration = {} as nsWebhookConfig
						nsIntegration.id = data.ClientWebhookID
						nsIntegration.type = "POST"
						nsIntegration.disabled = 150
						await this.patchNeuronSensors(data.NeuronSensorAPI, data.NeuronSensorSystem, nsIntegration)
					}
					this.globalFunctions.DisplaySnackBar({ message: `Webhook ${data.Description} had been Updated`, action: "" })
				}
			}
			catch (error) { this.globalFunctions.HandleError(error, "editIntegration Error: Patching Integrations", false) }
			finally { this.globalFunctions.displaySpinner(false) }
		});

	};

	/**
	 * patchNeuronSensors
	 * @param apiKey 
	 * @param systemID 
	 * @param _edit 
	 */
	async patchNeuronSensors(apiKey: string, systemID: string, _edit: nsWebhookConfig) {
		try {
			const success = await firstValueFrom(
				this.neuronsensorsService.patchIntegration$(apiKey, systemID, _edit).pipe(
					filter((result) => !!result))
			);
			if (success) { }
		}
		catch (error) {
			this.globalFunctions.HandleError(error, "patchNeuronSensors Error: this.neuronsensorsService.patchIntegration$", false)
		}
	}

	/**
	 * deleteIntegration
	 * @param integration 
	 */
	deleteIntegration(integration: IWebhookIntegration) {
		this.globalFunctions.UserConfirm$(`Delete ${integration.Description} webhook?`, `Delete Webhook`, "Yes", "No")
			.subscribe(async confirmed => {
				if (confirmed) {
					try {
						const result = await firstValueFrom(
							this.integrationService.deleteWebhookIntegration$(this.selectedAccountGUID, this.selectedOrganisation.Uid, integration.OrganisationWebhookID).pipe(
								filter((result) => !!result))
						);

						if (result) {
							await this.getIntegrationTypes(this.selectedAccountGUID, this.selectedOrganisation.Uid)
							this.globalFunctions.DisplaySnackBar({ message: "Webhook Deleted", action: "" })
						}
					}
					catch (error) { this.globalFunctions.HandleError(error, "Error: this.integrationService.deleteWebhookIntegration$", false) }
				}
			});
	};

	/**
	 * copy To Clipboard
	 * @param integration
	 */
	copyToClipboard(integration: IWebhookIntegration) {
		try {
			let text = integration.FQN;
			if (this.clipboard.copy(text)) {
				this.globalFunctions.DisplaySnackBar({
					message: "Path copied to clipboard",
					action: "",
					duration: 2000
				});
			};
		} catch (error) { console.error(error); }
	};

	/**
	 * onChange
	 * @param args 
	 * @returns 
	 */
	onChange(args: SelectEventArgs) {
		if (this.suppress) return;

		const index = args.selectedIndex;
		// const tab = this.accountTabs.tabId
		const tabName = args.selectedItem?.textContent?.trim();

		const type = this.webhookIntegrationTypes.find(
			t => t.Type === tabName
		);

		if (type && this.webhooktypeid != type.WebhookTypeID) {
			this.selectedIntegrationID = "";
			this.router.navigate(
				['.'],
				{
					relativeTo: this.activatedRoute,
					queryParams: {
						webhooktypeid: type.WebhookTypeID
					},
					queryParamsHandling: 'merge', // keep existing params
					replaceUrl: true              // optional: avoids polluting history
				}
			);
		}
	}

}
